/**
 * crm-reducer.ts  (v2.2 — Enterprise Hardened)
 *
 * Pure reducer for CRM plugin state. Map-based state, no arrays.
 * Enforces:
 * 1. Discriminated DealTransitions & Stage Entry Criteria.
 * 2. AccountChannelLink lifecycle (LINK, UNLINK, ARCHIVE).
 * 3. Action Planner Task Sync status updates.
 * 4. Bounded Import Batch Chunk Ingestion.
 */

import type {
  CrmState, CrmCommand, Contact, Organization, Deal, CrmActivity, FollowUp, AccountChannelLink,
  DealStage, ActivityType, DealTransition, StageEntryCriteria, UnqualifiedReason, OnHoldReason,
  DealConversation, PipelineThreadLink, ConversationLifecycleRecord, ConversationSurfaceMode,
  DealParticipant, FollowUpCreated, CrmDomainEvent,
} from './crm-contracts.js';
import { generateUUIDv7 } from '../../crypto/uuidv7.js';

export class CrmOccError extends Error {
  readonly code = 'OCC_REVISION_MISMATCH' as const;
  constructor(
    public readonly expectedRevision: string,
    public readonly currentRevision: string,
  ) {
    super(
      `OCC_REVISION_MISMATCH: command expected revision "${expectedRevision}" but state is at "${currentRevision}"`,
    );
  }
}

export const SCHEMA_VERSION = 4; // bumped: DATA-01D principal separation + RecordOrigin

export function getInitialCrmState(): CrmState {
  return {
    schemaVersion: SCHEMA_VERSION,
    revision: 'rev-0',
    contacts: new Map(),
    organizations: new Map(),
    deals: new Map(),
    activities: new Map(),
    followUps: new Map(),
    accountChannels: new Map(),
    // CRM-03A
    dealConversations: new Map(),
    pipelineThreadLinks: new Map(),
    conversationLifecycleLog: new Map(),
    // CRM-03F
    proposalAuditLog: new Map(),
    processedCommands: new Set(),
    revisionCounter: 0,
  };
}

function nextRevision(state: CrmState): string {
  return `rev-${state.revisionCounter + 1}`;
}

function bump(state: CrmState): Pick<CrmState, 'revision' | 'revisionCounter'> {
  const n = state.revisionCounter + 1;
  return { revisionCounter: n, revision: `rev-${n}` };
}

function mark(state: CrmState, commandId: string): Set<string> {
  const s = new Set(state.processedCommands);
  s.add(commandId);
  // Prune to keep at most 1000 command ids (prevent unbounded growth)
  if (s.size > 1000) {
    const it = s.values();
    while (s.size > 1000) {
      const { value, done } = it.next();
      if (done) break;
      s.delete(value);
    }
  }
  return s;
}

function cloneMap<V>(m: Map<string, V>): Map<string, V> {
  return new Map(m);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function optStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function optNum(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function strArr(v: unknown): readonly string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

const CREATION_COMMANDS = new Set([
  'ADD_CONTACT', 'ADD_ORGANIZATION', 'ADD_DEAL', 'ADD_ACTIVITY',
  'CREATE_FOLLOWUP', 'SYNC_TASK_STATUS', 'LINK_ACCOUNT_CHANNEL',
  'UNLINK_ACCOUNT_CHANNEL', 'ARCHIVE_ACCOUNT_CHANNEL', 'IMPORT_BATCH_CHUNK',
]);

function assertRevision(state: CrmState, command: CrmCommand): void {
  if (CREATION_COMMANDS.has(command.type)) return;
  if (command.expectedRevision !== state.revision) {
    throw new CrmOccError(command.expectedRevision, state.revision);
  }
}

const VALID_STAGES: DealStage[] = [
  'lead', 'qualified', 'discovery', 'proposal', 'negotiation', 'won', 'lost',
  'on_hold', 'unqualified', 'nurture',
];

const VALID_UNQUALIFIED_REASONS = new Set<UnqualifiedReason>([
  'budget_insufficient', 'no_decision', 'competitor_selected', 'out_of_scope', 'unresponsive',
]);

const VALID_ON_HOLD_REASONS = new Set<OnHoldReason>([
  'project_paused', 'awaiting_funding', 'seasonal_delay', 'internal_reorg',
]);

// ─── Stage Entry Criteria Validator ───────────────────────────────────────────

function validateStageEntryCriteria(
  deal: Deal,
  targetStage: DealStage,
  payload: Record<string, unknown>,
  actorId: string,
  now: number,
): { transition: DealTransition; updatedCriteria?: StageEntryCriteria } {
  const missing: string[] = [];
  const criteria = { ...(deal.entryCriteria ?? {}), ...(isRecord(payload.entryCriteria) ? payload.entryCriteria : {}) };

  if (targetStage === 'proposal') {
    if (!deal.primaryContactId && !criteria.decisionMakerId) missing.push('decisionMakerId');
  } else if (targetStage === 'negotiation') {
    if (deal.amount === undefined && payload.amount === undefined && !criteria.commercialValue) {
      missing.push('commercialValue');
    }
  } else if (targetStage === 'won') {
    if (!criteria.agreementSigned) missing.push('agreementSigned');
  }

  if (missing.length > 0) {
    throw new Error(`MISSING_TRANSITION_CRITERIA: Target stage "${targetStage}" requires [${missing.join(', ')}]`);
  }

  const meta = isRecord(payload.transitionMetadata) ? payload.transitionMetadata : payload;
  const reasonCode = str(meta.reasonCode) as any;

  if (targetStage === 'unqualified') {
    if (!VALID_UNQUALIFIED_REASONS.has(reasonCode)) {
      throw new Error(`MISSING_TRANSITION_CRITERIA: Transitioning to "unqualified" requires valid reasonCode.`);
    }
    return {
      transition: {
        toStage: 'unqualified',
        reasonCode,
        reasonNotes: optStr(meta.reasonNotes),
        transitionedAt: now,
        transitionedBy: actorId,
      },
      updatedCriteria: criteria,
    };
  }

  if (targetStage === 'on_hold') {
    if (!VALID_ON_HOLD_REASONS.has(reasonCode)) {
      throw new Error(`MISSING_TRANSITION_CRITERIA: Transitioning to "on_hold" requires valid reasonCode.`);
    }
    return {
      transition: {
        toStage: 'on_hold',
        reasonCode,
        reasonNotes: optStr(meta.reasonNotes),
        transitionedAt: now,
        transitionedBy: actorId,
      },
      updatedCriteria: criteria,
    };
  }

  return {
    transition: {
      toStage: targetStage as any,
      reasonCode: optStr(meta.reasonCode),
      reasonNotes: optStr(meta.reasonNotes),
      transitionedAt: now,
      transitionedBy: actorId,
    },
    updatedCriteria: criteria,
  };
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

export function crmReducer(state: CrmState, command: CrmCommand): CrmState {
  if (state.processedCommands.has(command.commandId)) return state;
  assertRevision(state, command);

  const p = isRecord(command.payload) ? command.payload : {};
  const now = typeof p.createdAt === 'number' ? p.createdAt : Date.now();

  switch (command.type) {

    case 'ADD_CONTACT': {
      const id = str(p.id) || generateUUIDv7(now);
      const contact: Contact = {
        id,
        organizationId: optStr(p.organizationId),
        name: str(p.name, 'Unnamed Contact'),
        email: optStr(p.email),
        phone: optStr(p.phone),
        title: optStr(p.title),
        tags: strArr(p.tags),
        linkedIn: optStr(p.linkedIn),
        notes: optStr(p.notes),
        ownerId: optStr(p.ownerId) ?? command.actorId,
        origin: isRecord(p.origin) ? (p.origin as any) : undefined,
        createdAt: now,
        updatedAt: now,
        revision: nextRevision(state),
      };
      const contacts = cloneMap(state.contacts);
      contacts.set(id, contact);

      const organizations = cloneMap(state.organizations);
      if (contact.organizationId && organizations.has(contact.organizationId)) {
        const org = organizations.get(contact.organizationId)!;
        if (!org.contactIds.includes(id)) {
          organizations.set(org.id, { ...org, contactIds: [...org.contactIds, id], updatedAt: now });
        }
      }

      return { ...state, contacts, organizations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'UPDATE_CONTACT': {
      const id = str(p.id);
      const existing = state.contacts.get(id);
      if (!existing) throw new Error(`Contact "${id}" not found`);

      const contacts = cloneMap(state.contacts);
      contacts.set(id, {
        ...existing,
        name: str(p.name, existing.name),
        email: p.email !== undefined ? optStr(p.email) : existing.email,
        phone: p.phone !== undefined ? optStr(p.phone) : existing.phone,
        title: p.title !== undefined ? optStr(p.title) : existing.title,
        tags: Array.isArray(p.tags) ? strArr(p.tags) : existing.tags,
        updatedAt: now,
        revision: nextRevision(state),
      });
      return { ...state, contacts, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ADD_ORGANIZATION': {
      const id = str(p.id) || generateUUIDv7(now);
      const domain = optStr(p.domain);
      const domains = strArr(p.domains).length > 0 ? strArr(p.domains) : (domain ? [domain] : []);
      const org: Organization = {
        id,
        name: str(p.name, 'Unnamed Organization'),
        legalName: optStr(p.legalName),
        tradingName: optStr(p.tradingName),
        domain,
        domains,
        sector: optStr(p.sector),
        industry: optStr(p.industry),
        sizeBand: (['micro', 'small', 'medium', 'large', 'enterprise'] as const).includes(p.sizeBand as any)
          ? (p.sizeBand as Organization['sizeBand'])
          : undefined,
        tags: strArr(p.tags),
        ownerId: optStr(p.ownerId) ?? command.actorId,
        contactIds: strArr(p.contactIds),
        channelIds: strArr(p.channelIds),
        origin: isRecord(p.origin) ? (p.origin as any) : undefined,
        createdAt: now,
        updatedAt: now,
        revision: nextRevision(state),
      };
      const organizations = cloneMap(state.organizations);
      organizations.set(id, org);
      return { ...state, organizations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'LINK_ACCOUNT_CHANNEL': {
      const orgId = str(p.organizationId || p.id);
      const channelId = str(p.channelId);
      const existingOrg = state.organizations.get(orgId);
      if (!existingOrg) throw new Error(`Organization "${orgId}" not found`);

      const linkKey = `${orgId}:${channelId}`;
      const accountChannels = cloneMap(state.accountChannels);
      accountChannels.set(linkKey, {
        organizationId: orgId,
        channelId,
        createdBy: command.actorId,
        createdAt: now,
        status: 'active',
        aclRevision: nextRevision(state),
      });

      const organizations = cloneMap(state.organizations);
      if (!existingOrg.channelIds.includes(channelId)) {
        organizations.set(orgId, {
          ...existingOrg,
          channelIds: [...existingOrg.channelIds, channelId],
          updatedAt: now,
        });
      }

      return { ...state, organizations, accountChannels, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'UNLINK_ACCOUNT_CHANNEL': {
      const orgId = str(p.organizationId || p.id);
      const channelId = str(p.channelId);
      const linkKey = `${orgId}:${channelId}`;

      const accountChannels = cloneMap(state.accountChannels);
      const existingLink = accountChannels.get(linkKey);
      if (existingLink) {
        accountChannels.set(linkKey, { ...existingLink, status: 'unlinked' });
      }

      const organizations = cloneMap(state.organizations);
      const existingOrg = organizations.get(orgId);
      if (existingOrg) {
        organizations.set(orgId, {
          ...existingOrg,
          channelIds: existingOrg.channelIds.filter((c) => c !== channelId),
          updatedAt: now,
        });
      }

      return { ...state, organizations, accountChannels, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ADD_DEAL': {
      const id = str(p.id) || generateUUIDv7(now);
      const stage: DealStage = VALID_STAGES.includes(p.stage as DealStage) ? (p.stage as DealStage) : 'lead';
      const deal: Deal = {
        id,
        organizationId: optStr(p.organizationId),
        primaryContactId: optStr(p.primaryContactId),
        name: str(p.name, 'Untitled Deal'),
        amount: optNum(p.amount),
        currency: optStr(p.currency) ?? 'USD',
        stage,
        rank: str(p.rank, 'm'),
        expectedCloseDate: optStr(p.expectedCloseDate),
        entryCriteria: isRecord(p.entryCriteria) ? (p.entryCriteria as any) : undefined,
        ownerId: optStr(p.ownerId) ?? command.actorId,
        origin: isRecord(p.origin) ? (p.origin as any) : undefined,
        createdAt: now,
        updatedAt: now,
        revision: nextRevision(state),
      };
      const deals = cloneMap(state.deals);
      deals.set(id, deal);
      return { ...state, deals, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'MOVE_DEAL_STAGE': {
      const id = str(p.id);
      const existing = state.deals.get(id);
      if (!existing) throw new Error(`Deal "${id}" not found`);

      const targetStage = p.stage as DealStage;
      if (!VALID_STAGES.includes(targetStage)) throw new Error(`Invalid deal stage "${targetStage}"`);

      // Enforce mandatory entry criteria & discriminated transition
      const { transition, updatedCriteria } = validateStageEntryCriteria(existing, targetStage, p, command.actorId, now);

      const deals = cloneMap(state.deals);
      deals.set(id, {
        ...existing,
        stage: targetStage,
        rank: typeof p.rank === 'string' ? p.rank : existing.rank,
        transitionMetadata: transition,
        entryCriteria: updatedCriteria,
        updatedAt: now,
        revision: nextRevision(state),
      });
      return { ...state, deals, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ADD_ACTIVITY': {
      const id = str(p.id) || generateUUIDv7(now);
      const activity: CrmActivity = {
        id,
        contactId: optStr(p.contactId),
        organizationId: optStr(p.organizationId),
        dealId: optStr(p.dealId),
        type: (p.type as ActivityType) ?? 'note',
        subject: optStr(p.subject),
        body: str(p.body),
        createdBy: command.actorId,
        origin: isRecord(p.origin) ? (p.origin as any) : undefined,
        createdAt: now,
      };
      const activities = cloneMap(state.activities);
      activities.set(id, activity);
      return { ...state, activities, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'CREATE_FOLLOWUP': {
      const followUpId = str(p.followUpId || p.id) || generateUUIDv7(now);
      const followUp: FollowUp = {
        followUpId,
        subjectType: (p.subjectType as any) ?? 'contact',
        subjectId: str(p.subjectId),
        taskId: str(p.taskId) || `task-${followUpId}`,
        ownerId: str(p.ownerId, command.actorId),
        dueAt: typeof p.dueAt === 'number' ? p.dueAt : now + 86400000 * 3,
        status: (['pending', 'completed', 'cancelled', 'overdue'] as const).includes(p.status as any) ? (p.status as any) : 'pending',
        createdAt: now,
        updatedAt: now,
      };
      const followUps = cloneMap(state.followUps);
      followUps.set(followUpId, followUp);

      const event: FollowUpCreated = {
        eventId: generateUUIDv7(now),
        eventType: 'crm.followup.created',
        aggregateId: followUpId,
        followUpId,
        taskId: followUp.taskId,
        subject: `Follow up regarding ${followUp.subjectType}`,
        dueAt: followUp.dueAt,
        ownerId: followUp.ownerId,
        sourceRevision: state.revision,
        causationId: command.commandId,
        occurredAt: now
      };
      const pendingEvents = [...(state.pendingEvents || []), event];

      return { ...state, followUps, pendingEvents, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'SYNC_TASK_STATUS': {
      const taskIdOrFollowUpId = str(p.taskId || p.followUpId || p.id);
      const taskStatus = str(p.taskStatus || p.status).toUpperCase();

      let target: FollowUp | undefined = state.followUps.get(taskIdOrFollowUpId);
      if (!target) {
        for (const f of state.followUps.values()) {
          if (f.taskId === taskIdOrFollowUpId) {
            target = f;
            break;
          }
        }
      }

      if (!target) return state;

      let newStatus: FollowUp['status'] = target.status;
      if (taskStatus === 'DONE' || taskStatus === 'COMPLETED') {
        newStatus = 'completed';
      } else if (taskStatus === 'CANCELLED' || taskStatus === 'DROPPED') {
        newStatus = 'cancelled';
      } else if (target.dueAt < now && target.status === 'pending') {
        newStatus = 'overdue';
      }

      const followUps = cloneMap(state.followUps);
      followUps.set(target.followUpId, {
        ...target,
        status: newStatus,
        lastSyncedTaskStatus: taskStatus,
        updatedAt: now,
      });
      return { ...state, followUps, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'IMPORT_BATCH_CHUNK': {
      const chunkRecords = Array.isArray(p.records) ? p.records : [];
      const contacts = cloneMap(state.contacts);
      const organizations = cloneMap(state.organizations);
      const deals = cloneMap(state.deals);

      for (const rec of chunkRecords) {
        if (!isRecord(rec)) continue;
        const recId = str(rec.id) || generateUUIDv7(now);

        if ('email' in rec || 'linkedIn' in rec) {
          contacts.set(recId, {
            id: recId,
            organizationId: optStr(rec.organizationId),
            name: str(rec.name, 'Imported Contact'),
            email: optStr(rec.email),
            phone: optStr(rec.phone),
            title: optStr(rec.title),
            tags: strArr(rec.tags).length > 0 ? strArr(rec.tags) : ['imported'],
            ownerId: command.actorId,
            createdAt: now,
            updatedAt: now,
            revision: nextRevision(state),
          });
        } else if ('domains' in rec || 'sector' in rec || 'industry' in rec) {
          organizations.set(recId, {
            id: recId,
            name: str(rec.name, 'Imported Organization'),
            legalName: optStr(rec.legalName),
            domain: optStr(rec.domain),
            domains: strArr(rec.domains),
            sector: optStr(rec.sector),
            industry: optStr(rec.industry),
            tags: strArr(rec.tags).length > 0 ? strArr(rec.tags) : ['imported'],
            contactIds: strArr(rec.contactIds),
            channelIds: strArr(rec.channelIds),
            ownerId: command.actorId,
            createdAt: now,
            updatedAt: now,
            revision: nextRevision(state),
          });
        } else if ('stage' in rec || 'amount' in rec) {
          deals.set(recId, {
            id: recId,
            organizationId: optStr(rec.organizationId),
            primaryContactId: optStr(rec.primaryContactId),
            name: str(rec.name, 'Imported Deal'),
            amount: optNum(rec.amount),
            currency: optStr(rec.currency) ?? 'USD',
            stage: VALID_STAGES.includes(rec.stage as DealStage) ? (rec.stage as DealStage) : 'lead',
            rank: str(rec.rank, 'm'),
            ownerId: command.actorId,
            createdAt: now,
            updatedAt: now,
            revision: nextRevision(state),
          });
        }
      }

      return {
        ...state,
        contacts,
        organizations,
        deals,
        processedCommands: mark(state, command.commandId),
        ...bump(state),
      };
    }

    case 'ARCHIVE_DEAL': {
      const id = str(p.dealId || p.id);
      const existing = state.deals.get(id);
      if (!existing) throw new Error(`Deal "${id}" not found`);

      const deals = cloneMap(state.deals);
      deals.set(id, { ...existing, archivedAt: now, updatedAt: now, revision: nextRevision(state) });
      return { ...state, deals, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ARCHIVE_CONTACT': {
      const id = str(p.contactId || p.id);
      const existing = state.contacts.get(id);
      if (!existing) throw new Error(`Contact "${id}" not found`);

      const contacts = cloneMap(state.contacts);
      contacts.set(id, { ...existing, archivedAt: now, updatedAt: now, revision: nextRevision(state) });
      return { ...state, contacts, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ARCHIVE_ORGANIZATION': {
      const id = str(p.organizationId || p.id);
      const existing = state.organizations.get(id);
      if (!existing) throw new Error(`Organization "${id}" not found`);

      const organizations = cloneMap(state.organizations);
      organizations.set(id, { ...existing, archivedAt: now, updatedAt: now, revision: nextRevision(state) });
      return { ...state, organizations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }
    // ─── CRM-03A Adaptive Surface Commands ───────────────────────────────────────

    case 'CREATE_DEAL_CONVERSATION': {
      const id = str(p.conversationId) || generateUUIDv7(now);
      if (state.dealConversations.has(id)) throw new Error(`DealConversation "${id}" already exists`);
      const conversation: DealConversation = {
        conversationId: id,
        dealId: str(p.dealId),
        organizationId: optStr(p.organizationId),
        threadChannelId: optStr(p.threadChannelId),
        participants: Array.isArray(p.participants) ? (p.participants as readonly DealParticipant[]) : [] as readonly DealParticipant[],
        activeAgentIds: [],
        contextHash: '',
        surfaceMode: 'chat-first',
        lifecycle: 'active',
        lastSurfaceModeChange: now,
        lastLifecycleChange: now,
        createdAt: now,
        updatedAt: now,
      };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(id, conversation);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ADD_DEAL_PARTICIPANT': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      const newParticipant = p.participant as DealParticipant;
      const participants = [...conversation.participants, newParticipant] as readonly DealParticipant[];
      const updated = { ...conversation, participants, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'UPDATE_PARTICIPANT_ROLE': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      const participants = conversation.participants.map(pr =>
        pr.principalId === p.principalId ? { ...pr, role: p.role as any, permissions: p.permissions as any, notificationMode: p.notificationMode as any } : pr,
      );
      const updated = { ...conversation, participants, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'SET_CONVERSATION_QUIET': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      if (conversation.lifecycle !== 'active') return state;
      const updated: DealConversation = { ...conversation, lifecycle: 'quiet' as const, lastLifecycleChange: now, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'REACTIVATE_DEAL_CONVERSATION': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      const updated: DealConversation = { ...conversation, lifecycle: 'active' as const, lastLifecycleChange: now, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'ARCHIVE_DEAL_CONVERSATION': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      const updated: DealConversation = { ...conversation, lifecycle: 'archived' as const, lastLifecycleChange: now, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'SET_CONVERSATION_SURFACE_MODE': {
      const conversation = state.dealConversations.get(str(p.conversationId));
      if (!conversation) throw new Error(`DealConversation "${p.conversationId}" not found`);
      const mode = p.surfaceMode as ConversationSurfaceMode;
      const updated = { ...conversation, surfaceMode: mode, lastSurfaceModeChange: now, updatedAt: now };
      const dealConversations = cloneMap(state.dealConversations);
      dealConversations.set(conversation.conversationId, updated);
      return { ...state, dealConversations, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'LINK_PIPELINE_THREAD': {
      const id = `${str(p.threadId)}:${str(p.dealId)}`;
      if (state.pipelineThreadLinks.has(id)) throw new Error(`PipelineThreadLink "${id}" already exists`);
      const link: PipelineThreadLink = {
        threadId: str(p.threadId),
        conversationId: str(p.conversationId),
        dealId: str(p.dealId),
        channelId: str(p.channelId),
        status: 'linked',
        linkedBy: command.actorId,
        linkedAt: now,
        revision: generateUUIDv7(now),
      };
      const pipelineThreadLinks = cloneMap(state.pipelineThreadLinks);
      pipelineThreadLinks.set(id, link);
      return { ...state, pipelineThreadLinks, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    case 'UNLINK_PIPELINE_THREAD': {
      const id = `${str(p.threadId)}:${str(p.dealId)}`;
      const existing = state.pipelineThreadLinks.get(id);
      if (!existing) throw new Error(`PipelineThreadLink "${id}" not found`);
      const updated: PipelineThreadLink = {
        ...existing,
        status: 'unlinked',
        unlinkedBy: command.actorId,
        unlinkedAt: now,
        revision: generateUUIDv7(now),
      };
      const pipelineThreadLinks = cloneMap(state.pipelineThreadLinks);
      pipelineThreadLinks.set(id, updated);
      return { ...state, pipelineThreadLinks, processedCommands: mark(state, command.commandId), ...bump(state) };
    }

    default:
      throw new Error(`Unknown CRM command type: "${String((command as any).type)}"`);
  }
}
